import * as React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useProjectContext } from "@/src/context/ProjectContext.tsx";
import { useNotificationStore } from "@/src/stores/notificationStore.ts";
import { useUserSettings } from "@/src/hooks/useUserSettings.ts";
import { useQuickStartTour } from "@/src/hooks/useQuickStartTour";
import QuickStartPrompt from "@/src/components/quickStart/QuickStartPrompt.tsx";
import {
  trackQuickstartCompleted,
  trackQuickstartSkipped,
  trackQuickstartStageReached,
} from "@/src/services/analyticsService";
import { generateVideo } from "@/src/services/videoService.ts";
import { analyzeVideoScene } from "@/src/services/geminiService.ts";
import { generateUniqueFilename } from "@/src/utils/helpers.ts";
import {
  deleteStudioProduct,
  getUrlFromPath,
  persistStudioProduct,
  persistVideoAnalysis,
  uploadAndResolve,
  uploadFileAsProduct,
} from "@/src/services/studioPersistenceService.ts";
import { auth } from "@/src/config/firebase.js";
import { resolveErrorMessage } from "@/src/utils/errorResolver.ts";
import { detectImageMimeType } from "@/src/utils/imageFileTypes.ts";
import {
  reportError,
  sanitizeFileNameForReport,
} from "@/src/utils/errorReporting.ts";
import {
  VIDEO_PRESETS,
  getVideoPreset,
  type VideoFrameInputDefinition,
  type VideoPresetDefinition,
} from "@/src/utils/videoPresets.ts";
import {
  getAspectRatioLabel,
  parseAspectRatioParts,
} from "@/src/utils/aspectRatios.ts";
import {
  BoltIcon,
  CreditIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  StarIcon,
} from "@/src/components/icons.tsx";
import {
  formatCreditAmount,
  getVideoGenerationCreditCost,
} from "@/src/utils/constants.ts";
import { formatRelativeTime } from "@/src/utils/relativeTime.ts";
import {
  FreePromptScene,
  GentleTiltScene,
  ZoomRevealScene,
} from "@/src/components/videoPresetDemos.tsx";
import type {
  AppImageFile,
  BringToLifeCameraMove,
  BringToLifeMode,
  GeneratedContent,
  StudioProduct,
  VideoFrameRole,
  VideoPresetId,
  VideoRenderSettings,
  VideoSceneAnalysis,
  VideoSceneType,
} from "@/src/types.ts";
import {
  BRING_TO_LIFE_CAMERA_MOVES,
  MAX_BRING_TO_LIFE_FOCUS_TARGET_LENGTH,
} from "@/src/types.ts";

const MAX_VIDEO_FRAME_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const MAX_CUSTOM_VIDEO_PROMPT_LENGTH = 4000;
const MAX_ZOOM_TARGET_LENGTH = 140;
const MAX_BRING_TO_LIFE_CUSTOM_TEXT_LENGTH = 1000;
const BRING_TO_LIFE_MODES: readonly BringToLifeMode[] = [
  "atmosphere",
  "camera-movement",
  "lifestyle-moment",
] as const;
const DISMISSED_VIDEO_HOW_IT_WORKS_STORAGE_KEY =
  "video-how-it-works-dismissed:v1";
const VIDEO_DURATION_OPTIONS = [4, 6, 8] as const satisfies ReadonlyArray<
  VideoRenderSettings["duration"]
>;
const VIDEO_RESOLUTION_OPTIONS = [
  "720p",
  "1080p",
] as const satisfies ReadonlyArray<VideoRenderSettings["resolution"]>;
const MAX_VIDEO_REFERENCE_IMAGES = 3;
type FreePromptGuidanceMode = "frames" | "references";
const VIDEO_PRESET_I18N_KEYS: Record<
  VideoPresetId,
  { label: string; description: string }
> = {
  "360-packshot": {
    label: "video.presets.360-packshot.label",
    description: "video.presets.360-packshot.description",
  },
  "product-functionality": {
    label: "video.presets.product-functionality.label",
    description: "video.presets.product-functionality.description",
  },
  "gentle-tilt": {
    label: "video.presets.gentle-tilt.label",
    description: "video.presets.gentle-tilt.description",
  },
  "zoom-reveal": {
    label: "video.presets.zoom-reveal.label",
    description: "video.presets.zoom-reveal.description",
  },
  "free-prompt": {
    label: "video.presets.free-prompt.label",
    description: "video.presets.free-prompt.description",
  },
  "bring-to-life": {
    label: "video.presets.bring-to-life.label",
    description: "video.presets.bring-to-life.description",
  },
};

function getPresetLabel(
  t: (key: string, options?: { defaultValue?: string }) => string,
  preset: VideoPresetDefinition
): string {
  return t(VIDEO_PRESET_I18N_KEYS[preset.id].label, {
    defaultValue: preset.label,
  });
}

function getPresetDescription(
  t: (key: string, options?: { defaultValue?: string }) => string,
  preset: VideoPresetDefinition
): string {
  return t(VIDEO_PRESET_I18N_KEYS[preset.id].description, {
    defaultValue: preset.description,
  });
}

type VideoModelOption = {
  id: VideoRenderSettings["modelId"];
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  labelKey: string;
  summaryKey: string;
  hintKey: string;
  allowedDurations: ReadonlyArray<VideoRenderSettings["duration"]>;
  allowedResolutions: ReadonlyArray<VideoRenderSettings["resolution"]>;
};

const VIDEO_MODEL_OPTIONS = [
  {
    id: "veo-3.1-fast-generate-001",
    Icon: BoltIcon,
    labelKey: "video.model.fast",
    summaryKey: "video.model.fastSummary",
    hintKey: "video.model.fastHint",
    allowedDurations: VIDEO_DURATION_OPTIONS,
    allowedResolutions: VIDEO_RESOLUTION_OPTIONS,
  },
  {
    id: "veo-3.1-generate-001",
    Icon: StarIcon,
    labelKey: "video.model.quality",
    summaryKey: "video.model.qualitySummary",
    hintKey: "video.model.qualityHint",
    allowedDurations: VIDEO_DURATION_OPTIONS,
    allowedResolutions: VIDEO_RESOLUTION_OPTIONS,
  },
] satisfies VideoModelOption[];

type VideoFrameSelections = Partial<
  Record<VideoFrameRole, { image: AppImageFile; productId: string | null }>
>;
type VideoImageSelection = { image: AppImageFile; productId: string | null };
type VideoImagePickTarget = VideoFrameRole | "reference";

function getVideoModelOption(
  modelId: VideoRenderSettings["modelId"]
): VideoModelOption {
  return (
    VIDEO_MODEL_OPTIONS.find((option) => option.id === modelId) ??
    VIDEO_MODEL_OPTIONS[0]
  );
}

function getCompatibleDurations(
  preset: VideoPresetDefinition,
  modelOption: VideoModelOption,
  settings: VideoRenderSettings
): Array<VideoRenderSettings["duration"]> {
  return preset
    .getAllowedDurations(settings)
    .filter((duration): duration is VideoRenderSettings["duration"] =>
      modelOption.allowedDurations.includes(
        duration as VideoRenderSettings["duration"]
      )
    );
}

function getCompatibleResolutions(
  modelOption: VideoModelOption
): Array<VideoRenderSettings["resolution"]> {
  return VIDEO_RESOLUTION_OPTIONS.filter((resolution) =>
    modelOption.allowedResolutions.includes(resolution)
  );
}

function clampRenderSettingsForPresetAndModel(
  preset: VideoPresetDefinition,
  settings: VideoRenderSettings
): VideoRenderSettings {
  const modelOption = getVideoModelOption(settings.modelId);
  const compatibleDurations = getCompatibleDurations(
    preset,
    modelOption,
    settings
  );
  const compatibleResolutions = getCompatibleResolutions(modelOption);

  return {
    ...settings,
    duration: compatibleDurations.includes(settings.duration)
      ? settings.duration
      : (compatibleDurations[0] ?? preset.getAllowedDurations(settings)[0]),
    resolution: compatibleResolutions.includes(settings.resolution)
      ? settings.resolution
      : (compatibleResolutions[0] ?? "720p"),
  };
}

function isImageFileCompatible(file: {
  mimeType?: string;
  storagePath?: string;
  thumbnailUrl?: string;
  fullUrl?: string;
  image?: string | null;
  video?: string | null;
}): boolean {
  if (file.video) return false;
  if (file.mimeType?.startsWith("video/")) return false;
  if (file.mimeType?.startsWith("image/")) return true;

  const candidatePath =
    file.storagePath || file.thumbnailUrl || file.fullUrl || file.image || "";
  return /\.(png|jpe?g|webp|gif|avif)(\?|$)/i.test(candidatePath);
}

function doesImageMatchAspectRatio(
  image: AppImageFile,
  targetAspectRatio: string
): boolean {
  if (!image.width || !image.height) return true;
  const target = parseAspectRatioParts(targetAspectRatio);
  if (!target) return true;
  const targetRatio = target.width / target.height;
  const actual = image.width / image.height;
  return Math.abs(actual - targetRatio) / targetRatio <= 0.02;
}

function sanitizeVideoTileFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_") || "video-cover.png";
}

function getVideoTileTitle(input: string): string {
  const trimmed = input.trim().replace(/\s+/g, " ");
  if (!trimmed) return "Video generation";
  return trimmed.length > 80 ? `${trimmed.slice(0, 77)}...` : trimmed;
}

async function imageFileToBlob(image: AppImageFile): Promise<Blob> {
  const mimeType = image.mimeType || "image/png";
  if (image.base64) {
    return await (
      await fetch(`data:${mimeType};base64,${image.base64}`)
    ).blob();
  }

  const imageUrl =
    image.fullUrl ||
    image.thumbnailUrl ||
    (image.storagePath ? await getUrlFromPath(image.storagePath) : "");
  if (!imageUrl) {
    throw new Error(`Cannot create video tile from ${image.name}`);
  }

  const response = await fetch(imageUrl);
  if (!response.ok) {
    throw new Error(`Failed to load video tile image: ${response.status}`);
  }
  return await response.blob();
}

function createPromptOnlyVideoCoverBlob(
  title: string,
  aspectRatio: VideoRenderSettings["aspectRatio"]
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  const isPortrait = aspectRatio === "9:16";
  canvas.width = isPortrait ? 720 : 1280;
  canvas.height = isPortrait ? 1280 : 720;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is not available");

  const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, "#111827");
  gradient.addColorStop(0.55, "#334155");
  gradient.addColorStop(1, "#0f172a");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = "rgba(255,255,255,0.08)";
  ctx.fillRect(
    canvas.width * 0.08,
    canvas.height * 0.12,
    canvas.width * 0.84,
    canvas.height * 0.76
  );

  ctx.fillStyle = "#ffffff";
  ctx.font = `700 ${isPortrait ? 46 : 56}px system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("Video generation", canvas.width / 2, canvas.height / 2 - 36);

  ctx.fillStyle = "rgba(255,255,255,0.72)";
  ctx.font = `500 ${isPortrait ? 24 : 28}px system-ui, sans-serif`;
  const subtitle = title.length > 48 ? `${title.slice(0, 45)}...` : title;
  ctx.fillText(subtitle, canvas.width / 2, canvas.height / 2 + 34);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Failed to create video tile cover"));
        return;
      }
      resolve(blob);
    }, "image/png");
  });
}

// ---------------------------------------------------------------------------
// Library image picker modal
// ---------------------------------------------------------------------------

interface LibraryPickerModalProps {
  targetAspectRatio: string;
  onClose: () => void;
  onSelect: (image: AppImageFile, productId: string) => void;
}

type PickerSortOrder = "newest" | "oldest";

interface PickerItem {
  image: AppImageFile;
  productId: string;
  productName: string;
  filename: string;
  imageType?: string;
  timestamp: number;
  isSource: boolean;
}

function stripFileExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(0, dot) : name;
}

function LibraryPickerModal({
  targetAspectRatio,
  onClose,
  onSelect,
}: LibraryPickerModalProps) {
  const { t, i18n } = useTranslation();
  const { studioProducts, isLoadingProducts } = useProjectContext();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [sortOrder, setSortOrder] = React.useState<PickerSortOrder>("newest");
  const [matchAspectOnly, setMatchAspectOnly] = React.useState(false);

  React.useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Collect all images: product source images + generated images (image only)
  const allImages = React.useMemo<PickerItem[]>(() => {
    const items: PickerItem[] = [];
    for (const product of studioProducts) {
      const productName = stripFileExtension(product.originalImage.name || "");
      // Source image
      if (
        (product.image.thumbnailUrl || product.image.storagePath) &&
        isImageFileCompatible(product.image)
      ) {
        items.push({
          image: product.image,
          productId: product.id,
          productName,
          filename: product.originalImage.name,
          imageType: undefined,
          timestamp: 0,
          isSource: true,
        });
      }
      // Generated images (not videos)
      for (const content of product.generatedContent) {
        if (
          content.image &&
          content.storagePath?.startsWith("f/") &&
          isImageFileCompatible(content)
        ) {
          items.push({
            image: {
              id: content.id,
              base64: "",
              mimeType: content.mimeType || "image/jpeg",
              name: content.filename || content.imageType || "Generated",
              storagePath: content.storagePath,
              thumbnailUrl: content.thumbnailUrl || content.image,
              fullUrl: content.fullUrl || content.image,
              width: content.dimensions?.width,
              height: content.dimensions?.height,
              sizeBytes: content.sizeBytes,
            },
            productId: product.id,
            productName,
            filename: content.filename || content.imageType || "Generated",
            imageType: content.imageType,
            timestamp: content.timestamp || 0,
            isSource: false,
          });
        }
      }
    }
    return items;
  }, [studioProducts]);

  const hasMismatchedImages = allImages.some(
    (item) => !doesImageMatchAspectRatio(item.image, targetAspectRatio)
  );

  const groupedItems = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = allImages.filter((item) => {
      if (
        matchAspectOnly &&
        !doesImageMatchAspectRatio(item.image, targetAspectRatio)
      ) {
        return false;
      }
      if (!query) return true;
      return (
        item.filename.toLowerCase().includes(query) ||
        item.productName.toLowerCase().includes(query) ||
        (item.imageType?.toLowerCase().includes(query) ?? false)
      );
    });

    const groups = new Map<string, PickerItem[]>();
    for (const item of filtered) {
      const list = groups.get(item.productId) ?? [];
      list.push(item);
      groups.set(item.productId, list);
    }

    // Sort within each group: source first, then by timestamp per sortOrder.
    for (const list of groups.values()) {
      list.sort((a, b) => {
        if (a.isSource !== b.isSource) return a.isSource ? -1 : 1;
        return sortOrder === "newest"
          ? b.timestamp - a.timestamp
          : a.timestamp - b.timestamp;
      });
    }

    // Preserve studioProducts order.
    return studioProducts
      .map((product) => ({
        productId: product.id,
        productName: stripFileExtension(product.originalImage.name || ""),
        items: groups.get(product.id) ?? [],
      }))
      .filter((group) => group.items.length > 0);
  }, [
    allImages,
    matchAspectOnly,
    searchQuery,
    sortOrder,
    studioProducts,
    targetAspectRatio,
  ]);

  const totalCount = groupedItems.reduce(
    (acc, group) => acc + group.items.length,
    0
  );
  const productCount = groupedItems.length;
  const hasResults = totalCount > 0;
  const isFiltering = searchQuery.trim().length > 0 || matchAspectOnly;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-gray-200 px-6 py-4 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xl tracking-tight font-semibold text-gray-900">
              {t("video.picker.title")}
            </h2>
            <p className="text-sm text-gray-500 font-medium">
              {hasResults
                ? t("video.picker.summary", {
                    count: totalCount,
                    products: productCount,
                  })
                : t("video.picker.imagesCount", { count: 0 })}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder={t("video.picker.searchPlaceholder")}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="h-10 pl-10 pr-4 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-black focus:border-transparent w-64 placeholder:text-gray-400 outline-none transition-all shadow-sm"
              />
              <svg
                className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            <div className="h-8 w-px bg-gray-200" />

            <label
              className={`inline-flex h-10 items-center gap-2 rounded-lg border px-3 text-sm font-semibold cursor-pointer select-none transition-colors ${
                matchAspectOnly
                  ? "border-black bg-black text-white"
                  : "border-gray-300 bg-white text-gray-700 hover:border-gray-300"
              }`}
            >
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 accent-black"
                checked={matchAspectOnly}
                onChange={(event) => setMatchAspectOnly(event.target.checked)}
              />
              {t("video.picker.matchAspectOnly", { ratio: targetAspectRatio })}
            </label>

            <div className="h-8 w-px bg-gray-200" />

            <div className="flex bg-gray-100 rounded-lg p-1 h-10 box-border items-center">
              <button
                type="button"
                onClick={() => setSortOrder("newest")}
                className={`px-3 h-full text-sm font-semibold rounded-lg transition-all flex items-center justify-center ${
                  sortOrder === "newest"
                    ? "bg-white shadow-sm text-black"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {t("studio.newest")}
              </button>
              <button
                type="button"
                onClick={() => setSortOrder("oldest")}
                className={`px-3 h-full text-sm font-semibold rounded-lg transition-all flex items-center justify-center ${
                  sortOrder === "oldest"
                    ? "bg-white shadow-sm text-black"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {t("studio.oldest")}
              </button>
            </div>

            <div className="h-8 w-px bg-gray-200" />

            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label={t("common.close")}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {hasMismatchedImages && !matchAspectOnly && (
          <div className="mt-3 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <p>
              {t("video.picker.aspectRatioWarning", {
                ratio: targetAspectRatio,
              })}
            </p>
          </div>
        )}
      </header>

      {/* Body */}
      <div className="flex-grow overflow-y-auto bg-gray-50/50">
        {isLoadingProducts ? (
          <p className="text-sm text-gray-500 text-center py-16">
            {t("common.loading")}
          </p>
        ) : !hasResults ? (
          <div className="flex flex-col items-center justify-center h-full min-h-[60vh] text-gray-400">
            <PhotoIcon className="w-16 h-16 mb-4 opacity-50" />
            <h3 className="text-base font-semibold">
              {isFiltering
                ? t("video.picker.noResults")
                : t("video.picker.empty")}
            </h3>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {groupedItems.map((group) => (
              <section key={group.productId} className="px-6 py-5">
                <header className="flex items-baseline gap-3 mb-3">
                  <h3
                    className="text-sm font-semibold text-gray-900 truncate"
                    title={group.productName}
                  >
                    {group.productName}
                  </h3>
                  <span className="text-xs font-medium text-gray-500">
                    {t("video.picker.imagesCount", {
                      count: group.items.length,
                    })}
                  </span>
                </header>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {group.items.map((item) => (
                    <PickerCard
                      key={`${item.productId}-${item.image.id}`}
                      item={item}
                      targetAspectRatio={targetAspectRatio}
                      locale={i18n.language}
                      onSelect={() => onSelect(item.image, item.productId)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface PickerCardProps {
  item: PickerItem;
  targetAspectRatio: string;
  locale: string;
  onSelect: () => void;
}

function PickerCard({
  item,
  targetAspectRatio,
  locale,
  onSelect,
}: PickerCardProps) {
  const { t } = useTranslation();
  const isMismatched = !doesImageMatchAspectRatio(
    item.image,
    targetAspectRatio
  );
  const width = item.image.width;
  const height = item.image.height;
  const resLabel = width && height ? `${width}×${height}` : null;
  const aspectLabel =
    width && height ? getAspectRatioLabel(width, height) : null;
  const relativeTime = item.isSource
    ? null
    : formatRelativeTime(item.timestamp, locale);

  return (
    <button
      type="button"
      onClick={onSelect}
      title={item.filename}
      className="group flex flex-col rounded-xl overflow-hidden border border-gray-200 bg-white text-left hover:border-black hover:shadow-md focus:outline-none focus:ring-2 focus:ring-black transition-all"
    >
      <div className="relative aspect-square overflow-hidden bg-gray-100">
        <img
          src={item.image.thumbnailUrl || item.image.fullUrl || ""}
          alt={item.filename}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-200"
        />
        {item.isSource && (
          <span className="absolute top-2 left-2 bg-white/95 text-[9px] font-semibold uppercase tracking-wide text-gray-700 rounded px-1.5 py-0.5 shadow-sm">
            {t("video.picker.sourceLabel")}
          </span>
        )}
        {isMismatched && (
          <span
            className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-white shadow-sm"
            title={t("video.picker.aspectRatioBadge", {
              ratio: targetAspectRatio,
            })}
          >
            <ExclamationTriangleIcon className="h-4 w-4" />
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1 px-2.5 py-2 border-t border-gray-100">
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex-1 truncate text-[11px] font-medium text-gray-900">
            {item.filename}
          </span>
          {relativeTime && (
            <span className="flex-shrink-0 text-[10px] font-mono text-gray-400">
              {relativeTime}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 min-w-0">
          {resLabel ? (
            <>
              <span className="text-[10px] font-medium text-gray-500 font-mono leading-none truncate">
                {resLabel}
              </span>
              {aspectLabel && (
                <span className="text-[9px] font-semibold text-gray-400 bg-gray-100 rounded px-1.5 py-0.5 leading-none flex-shrink-0">
                  {aspectLabel}
                </span>
              )}
            </>
          ) : (
            <span className="text-[10px] text-gray-400 leading-none">—</span>
          )}
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Preset card
// ---------------------------------------------------------------------------

interface PresetCardProps {
  preset: VideoPresetDefinition;
  selected: boolean;
  onSelect: () => void;
}

type VideoPresetDemoMedia =
  | {
      kind: "files";
      preview: {
        video: string;
        poster: string;
      };
      howItWorks: {
        startFrame: string;
        endFrame: string;
        resultVideo: string;
      };
    }
  | {
      kind: "render";
      preview: () => React.ReactNode;
      howItWorks: {
        start: () => React.ReactNode;
        end: () => React.ReactNode;
        result: () => React.ReactNode;
      };
    };

const VIDEO_PRESET_DEMO_MEDIA: Record<VideoPresetId, VideoPresetDemoMedia> = {
  "360-packshot": {
    kind: "files",
    preview: {
      video: "/video-how-it-works/product-spin-result.m4v",
      poster: "/video-how-it-works/product-spin-start-frame.png",
    },
    howItWorks: {
      startFrame: "/video-how-it-works/product-spin-start-frame.png",
      endFrame: "/video-how-it-works/product-spin-end-frame.png",
      resultVideo: "/video-how-it-works/product-spin-result.m4v",
    },
  },
  "product-functionality": {
    kind: "files",
    preview: {
      video: "/video-how-it-works/product-functionality-result.mp4",
      poster: "/video-how-it-works/product-functionality-start-frame.png",
    },
    howItWorks: {
      startFrame: "/video-how-it-works/product-functionality-start-frame.png",
      endFrame: "/video-how-it-works/product-functionality-end-frame.png",
      resultVideo: "/video-how-it-works/product-functionality-result.mp4",
    },
  },
  "gentle-tilt": {
    kind: "render",
    preview: () => <GentleTiltScene mode="animate" />,
    howItWorks: {
      start: () => <GentleTiltScene mode="start" />,
      end: () => <GentleTiltScene mode="end" />,
      result: () => <GentleTiltScene mode="animate" />,
    },
  },
  "free-prompt": {
    kind: "render",
    preview: () => <FreePromptScene mode="result" />,
    howItWorks: {
      start: () => <FreePromptScene mode="prompt" />,
      end: () => <FreePromptScene mode="frames" />,
      result: () => <FreePromptScene mode="result" />,
    },
  },
  "zoom-reveal": {
    kind: "render",
    preview: () => <ZoomRevealScene mode="animate" />,
    howItWorks: {
      start: () => <ZoomRevealScene mode="start" />,
      end: () => <ZoomRevealScene mode="end" />,
      result: () => <ZoomRevealScene mode="animate" />,
    },
  },
  "bring-to-life": {
    kind: "files",
    preview: {
      video: "/video-how-it-works/bringtolife.mp4",
      poster: "/video-how-it-works/bringtolife.jpg",
    },
    howItWorks: {
      // Bring to Life has no end frame — `endFrame` is required by the type
      // but the UI only renders it when `hasEndFrameInput` is true (it's not
      // for this preset), so reusing the start-frame asset is harmless.
      startFrame: "/video-how-it-works/bringtolife.jpg",
      endFrame: "/video-how-it-works/bringtolife.jpg",
      resultVideo: "/video-how-it-works/bringtolife.mp4",
    },
  },
};

function PresetPreviewPlaceholder({ presetId }: { presetId: VideoPresetId }) {
  const demoMedia = VIDEO_PRESET_DEMO_MEDIA[presetId];

  if (demoMedia.kind === "render") {
    return (
      <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-white">
        {demoMedia.preview()}
        <div className="pointer-events-none absolute bottom-1 right-1 rounded-full bg-black/70 px-1.5 py-0.5 text-[9px] font-semibold text-white">
          ▶
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-white">
      <video
        src={demoMedia.preview.video}
        poster={demoMedia.preview.poster}
        muted
        loop
        autoPlay
        playsInline
        preload="metadata"
        className="h-full w-full object-contain bg-white"
      />
      <div className="pointer-events-none absolute bottom-1 right-1 rounded-full bg-black/70 px-1.5 py-0.5 text-[9px] font-semibold text-white">
        ▶
      </div>
    </div>
  );
}

function PresetCard({ preset, selected, onSelect }: PresetCardProps) {
  const { t } = useTranslation();

  return (
    <button
      onClick={onSelect}
      className={`flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors ${
        selected ? "bg-gray-100" : "bg-white hover:bg-gray-50"
      }`}
    >
      <PresetPreviewPlaceholder presetId={preset.id} />
      <div className="min-w-0 flex-1 py-1">
        <div className="truncate text-sm font-semibold text-gray-900">
          {getPresetLabel(t, preset)}
        </div>
        <div className="mt-1 line-clamp-2 text-sm leading-snug text-gray-500">
          {getPresetDescription(t, preset)}
        </div>
      </div>
    </button>
  );
}

interface ControlSectionProps {
  title: React.ReactNode;
  children: React.ReactNode;
  /** Quick-start highlight ring (SIS-566). */
  highlighted?: boolean;
}

function ControlSection({
  title,
  children,
  highlighted = false,
}: ControlSectionProps) {
  return (
    <section>
      <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-3">
        {title}
      </h3>
      <div
        className={`border bg-white p-4 rounded-xl ${
          highlighted
            ? "border-blue-400 ring-4 ring-blue-500/25"
            : "border-gray-200"
        }`}
      >
        {children}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Bring to Life controls
// ---------------------------------------------------------------------------

interface BringToLifeControlsProps {
  mode: BringToLifeMode;
  onModeChange: (mode: BringToLifeMode) => void;
  customText: string;
  onCustomTextChange: (value: string) => void;
  cameraMove: BringToLifeCameraMove;
  onCameraMoveChange: (move: BringToLifeCameraMove) => void;
  focusTarget: string;
  onFocusTargetChange: (value: string) => void;
  analysis: VideoSceneAnalysis | null;
  isAnalyzing: boolean;
  analysisError: string | null;
  hasStartFrame: boolean;
  isLifestyleBlocked: boolean;
}

interface SceneAnalysisIndicatorProps {
  isAnalyzing: boolean;
  hasAnalysis: boolean;
  errorMessage: string | null;
  onRetry: () => void;
}

// Each state is keyed and its text wrapped in a <span> so a state change
// replaces the whole element instead of splicing an icon in next to a bare
// text node. Browser page translation swaps those text nodes out from under
// React, which made `insertBefore` throw (IMAGE-STUDIO-5T).
function SceneAnalysisIndicator({
  isAnalyzing,
  hasAnalysis,
  errorMessage,
  onRetry,
}: SceneAnalysisIndicatorProps) {
  const { t } = useTranslation();

  if (isAnalyzing) {
    return (
      <div
        key="analyzing"
        className="mt-3 inline-flex items-center gap-2 text-xs text-gray-700"
      >
        <span className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
        <span>{t("video.bringToLife.analyzing")}</span>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div key="error" className="mt-3 inline-flex items-center gap-3 text-xs">
        <span className="inline-flex items-center gap-1.5 text-red-600">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{t("video.bringToLife.analysisFailed")}</span>
        </span>
        <button
          type="button"
          onClick={onRetry}
          className="font-semibold text-gray-700 underline-offset-2 hover:underline"
        >
          {t("video.bringToLife.retryAnalysis")}
        </button>
      </div>
    );
  }

  if (hasAnalysis) {
    return (
      <div
        key="success"
        className="mt-3 inline-flex items-center gap-2 text-xs text-green-600"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span>{t("video.bringToLife.analysisSuccess")}</span>
      </div>
    );
  }

  return null;
}

function BringToLifeStepBadge({
  number,
  label,
  active,
}: {
  number: number;
  label: string;
  active: boolean;
}) {
  return (
    <p
      className={`mb-2 inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide ${
        active ? "text-gray-700" : "text-gray-400"
      }`}
    >
      <span
        className={`rounded-full px-1.5 py-0.5 text-[9px] ${
          active ? "bg-gray-900 text-white" : "bg-gray-200 text-gray-500"
        }`}
      >
        {number}
      </span>
      {label}
    </p>
  );
}

function BringToLifeControls({
  mode,
  onModeChange,
  customText,
  onCustomTextChange,
  cameraMove,
  onCameraMoveChange,
  focusTarget,
  onFocusTargetChange,
  analysis,
  isAnalyzing,
  analysisError,
  hasStartFrame,
  isLifestyleBlocked,
}: BringToLifeControlsProps) {
  const { t } = useTranslation();
  const stylePickerActive = hasStartFrame;
  const analysisReady = !!analysis && !isAnalyzing && !analysisError;

  return (
    <div className="space-y-5">
      {/* Step 2: Animation style */}
      <div className={stylePickerActive ? "" : "opacity-60"}>
        <BringToLifeStepBadge
          number={2}
          label={t("video.bringToLife.step2Label")}
          active={stylePickerActive}
        />
        <div className="flex bg-gray-100 p-1 rounded-lg">
          {BRING_TO_LIFE_MODES.map((m) => {
            const disabled = m === "lifestyle-moment" && isLifestyleBlocked;
            return (
              <button
                key={m}
                type="button"
                disabled={disabled || !stylePickerActive}
                title={
                  disabled
                    ? t("video.bringToLife.lifestyleNoPersonHint")
                    : undefined
                }
                onClick={() => onModeChange(m)}
                className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${
                  mode === m
                    ? "bg-white text-black shadow-sm"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
                } ${disabled || !stylePickerActive ? "cursor-not-allowed" : ""} ${
                  disabled ? "opacity-40" : ""
                }`}
              >
                {t(`video.bringToLife.mode.${m}.label`)}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-xs leading-relaxed text-gray-500">
          {t(`video.bringToLife.mode.${mode}.description`)}
        </p>

        {mode === "camera-movement" && stylePickerActive && (
          <div className="mt-4 space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                {t("video.bringToLife.cameraMoveLabel")}
              </label>
              <select
                value={cameraMove}
                onChange={(event) =>
                  onCameraMoveChange(
                    event.target.value as BringToLifeCameraMove
                  )
                }
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
              >
                {BRING_TO_LIFE_CAMERA_MOVES.map((m) => (
                  <option key={m} value={m}>
                    {t(`video.bringToLife.cameraMove.${m}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                {t("video.bringToLife.focusTargetLabel")}
              </label>
              <input
                type="text"
                value={focusTarget}
                onChange={(event) => onFocusTargetChange(event.target.value)}
                maxLength={MAX_BRING_TO_LIFE_FOCUS_TARGET_LENGTH}
                placeholder={t("video.bringToLife.focusTargetPlaceholder")}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
              />
              <div className="mt-1.5 flex items-center justify-between gap-3 text-[10px] text-gray-500">
                <span>{t("video.bringToLife.focusTargetHint")}</span>
                <span>
                  {focusTarget.length}/{MAX_BRING_TO_LIFE_FOCUS_TARGET_LENGTH}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Step 3: Optional action. Disabled until analysis is done. The
          analysis status itself is now rendered as an ambient indicator
          under the start frame card, so this step is purely action input. */}
      <div className={analysisReady ? "" : "opacity-60"}>
        <BringToLifeStepBadge
          number={3}
          label={t("video.bringToLife.step3Label")}
          active={analysisReady}
        />
        <textarea
          value={customText}
          onChange={(event) => onCustomTextChange(event.target.value)}
          maxLength={MAX_BRING_TO_LIFE_CUSTOM_TEXT_LENGTH}
          rows={3}
          disabled={!analysisReady}
          placeholder={t("video.bringToLife.customDirectionPlaceholder")}
          className="w-full resize-none rounded-lg border border-gray-300 bg-white p-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition disabled:bg-gray-50 disabled:cursor-not-allowed"
        />
        <div className="mt-2 flex items-center justify-between gap-3 text-[10px] text-gray-500">
          <span>{t("video.bringToLife.customDirectionHint")}</span>
          <span>
            {customText.length}/{MAX_BRING_TO_LIFE_CUSTOM_TEXT_LENGTH}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Video frame input
// ---------------------------------------------------------------------------

interface VideoFrameInputCardProps {
  frameInput: VideoFrameInputDefinition;
  image: AppImageFile | null;
  isUploading: boolean;
  onPickLibrary: () => void;
  onUpload: (file: File) => void;
  onClear: () => void;
}

function VideoFrameInputCard({
  frameInput,
  image,
  isUploading,
  onPickLibrary,
  onUpload,
  onClear,
}: VideoFrameInputCardProps) {
  const { t } = useTranslation();
  const fileInputId = `video-${frameInput.role}-frame-upload`;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) onUpload(file);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">
            {t(`video.frame.${frameInput.role}`)}
            <span className="ml-1 text-xs font-normal text-gray-500">
              (
              {frameInput.required
                ? t("video.panel.required")
                : t("video.panel.optional")}
              )
            </span>
          </h4>
        </div>
        {!frameInput.required && image && (
          <button
            onClick={onClear}
            className="text-xs font-medium text-gray-400 hover:text-gray-700"
          >
            {t("video.panel.clearFrame")}
          </button>
        )}
      </div>

      <div className="min-h-[150px] rounded-xl border border-gray-200 bg-white px-3 py-2.5 sm:px-4">
        {image ? (
          <div className="flex h-full items-center gap-3">
            <div className="relative h-20 w-28 flex-shrink-0 overflow-hidden rounded-xl bg-gray-100">
              <img
                src={image.thumbnailUrl || image.fullUrl || ""}
                alt={image.name}
                className="h-full w-full object-cover"
              />
              {isUploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-xs font-semibold text-white">
                  {t("video.panel.uploading")}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-gray-900">
                {image.name}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={onPickLibrary}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-gray-300"
                >
                  {t("video.panel.changeImage")}
                </button>
                <label className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-gray-300 text-center cursor-pointer">
                  {isUploading
                    ? t("video.panel.uploading")
                    : t("video.panel.uploadImage")}
                  <input
                    id={`${fileInputId}-replace`}
                    type="file"
                    className="hidden"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleFileChange}
                    disabled={isUploading}
                  />
                </label>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex min-h-[150px] flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 px-5 py-4 text-center sm:px-6">
            <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-white text-gray-500 shadow-sm">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 16V4" />
                <path d="m7 9 5-5 5 5" />
                <path d="M20 16v4H4v-4" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-gray-900">
              {t("video.panel.uploadImage")}
            </p>
            <p className="mt-1 text-xs text-gray-500">PNG, JPG, WebP · 20 MB</p>
            <div className="mt-3 flex w-full max-w-[420px] flex-wrap items-center justify-center gap-2">
              <label className="inline-flex min-h-8 min-w-0 flex-1 basis-[150px] items-center justify-center rounded-lg bg-black px-3 py-1.5 text-center text-xs font-semibold leading-tight text-white hover:bg-gray-800 cursor-pointer">
                {isUploading
                  ? t("video.panel.uploading")
                  : t("video.panel.uploadImage")}
                <input
                  id={fileInputId}
                  type="file"
                  className="hidden"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
              </label>
              <button
                onClick={onPickLibrary}
                className="inline-flex min-h-8 min-w-0 flex-1 basis-[150px] items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-center text-xs font-semibold leading-tight text-gray-700 hover:border-gray-300"
              >
                {t("video.panel.pickImage")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface VideoReferenceImagesCardProps {
  selections: VideoImageSelection[];
  isUploading: boolean;
  onPickLibrary: () => void;
  onUpload: (file: File) => void;
  onRemove: (index: number) => void;
}

function VideoReferenceImagesCard({
  selections,
  isUploading,
  onPickLibrary,
  onUpload,
  onRemove,
}: VideoReferenceImagesCardProps) {
  const { t } = useTranslation();
  const fileInputId = "video-reference-image-upload";
  const canAddMore = selections.length < MAX_VIDEO_REFERENCE_IMAGES;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) onUpload(file);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">
            {t("video.panel.referenceImages")}
          </h4>
          <p className="mt-1 text-xs leading-relaxed text-gray-500">
            {t("video.panel.referenceImagesHint", {
              count: MAX_VIDEO_REFERENCE_IMAGES,
            })}
          </p>
        </div>
        <span className="text-xs font-medium text-gray-400">
          {selections.length}/{MAX_VIDEO_REFERENCE_IMAGES}
        </span>
      </div>

      {selections.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-3">
          {selections.map((selection, index) => (
            <div
              key={`${selection.image.id || selection.image.storagePath || index}`}
              className="relative overflow-hidden rounded-xl border border-gray-200 bg-gray-50"
            >
              <div className="aspect-video bg-white">
                <img
                  src={
                    selection.image.thumbnailUrl ||
                    selection.image.fullUrl ||
                    ""
                  }
                  alt={selection.image.name}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex items-center justify-between gap-2 px-2 py-1.5">
                <p className="min-w-0 truncate text-[11px] font-semibold text-gray-700">
                  {selection.image.name}
                </p>
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  className="shrink-0 text-[11px] font-semibold text-gray-400 hover:text-gray-900"
                >
                  {t("video.panel.removeReference")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center">
        <p className="text-sm font-semibold text-gray-900">
          {t("video.panel.addReferenceImage")}
        </p>
        <p className="mt-1 text-xs text-gray-500">
          {t("video.panel.referenceImagesDurationHint")}
        </p>
        <div className="mt-3 grid w-full max-w-[320px] grid-cols-2 gap-2 mx-auto">
          <label
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold text-center ${
              canAddMore && !isUploading
                ? "bg-black text-white hover:bg-gray-800 cursor-pointer"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            {isUploading
              ? t("video.panel.uploading")
              : t("video.panel.uploadImage")}
            <input
              id={fileInputId}
              type="file"
              className="hidden"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleFileChange}
              disabled={!canAddMore || isUploading}
            />
          </label>
          <button
            type="button"
            onClick={onPickLibrary}
            disabled={!canAddMore || isUploading}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${
              canAddMore && !isUploading
                ? "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                : "border-gray-100 bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            {t("video.panel.pickImage")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recent video strip
// ---------------------------------------------------------------------------

interface RecentVideoStripProps {
  videos: Array<{ content: GeneratedContent; productId: string }>;
  pendingItems: Array<{ id: string; label: string; startedAt: number }>;
  onPlay: (content: GeneratedContent, productId: string) => void;
}

function RecentVideoStrip({
  videos,
  pendingItems,
  onPlay,
}: RecentVideoStripProps) {
  const { t, i18n } = useTranslation();
  const [collapsed, setCollapsed] = React.useState(false);
  const [downloadingId, setDownloadingId] = React.useState<string | null>(null);

  const handleDownload = async (content: GeneratedContent) => {
    if (!content.fullUrl || downloadingId) return;
    const baseTitle = (content.title || "video").trim() || "video";
    const baseName = baseTitle.replace(/\.[a-z0-9]+$/i, "");
    const filename = `${baseName}.mp4`;
    setDownloadingId(content.id);
    try {
      const blob = await fetch(content.fullUrl).then((r) => r.blob());
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      window.open(content.fullUrl, "_blank");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <section className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setCollapsed((prev) => !prev)}
        className="flex w-full items-center justify-between gap-4 border-b border-gray-200 px-6 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <path d="M7 4v16" />
              <path d="M17 4v16" />
              <path d="M3 9h18" />
              <path d="M3 15h18" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            {t("video.recent.title")}
          </h2>
          <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-500">
            {videos.length + pendingItems.length}
          </span>
        </div>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`text-gray-500 transition-transform ${collapsed ? "rotate-180" : ""}`}
        >
          <path d="m18 15-6-6-6 6" />
        </svg>
      </button>

      {!collapsed && (
        <div className="p-6">
          {videos.length === 0 && pendingItems.length === 0 ? (
            <p className="text-sm text-gray-500">{t("video.recent.empty")}</p>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-1">
              {pendingItems.map((pendingItem) => (
                <div
                  key={pendingItem.id}
                  className="w-72 flex-shrink-0 overflow-hidden rounded-xl border border-blue-200 bg-blue-50/60"
                >
                  <div className="relative flex aspect-video w-full items-center justify-center overflow-hidden bg-gradient-to-br from-blue-100 via-indigo-100 to-blue-200">
                    <div className="absolute inset-0 animate-pulse bg-white/20" />
                    <div className="relative z-10 flex flex-col items-center gap-2 px-4 text-center">
                      <span className="h-6 w-6 animate-spin rounded-full border-2 border-blue-400 border-t-transparent" />
                      <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                        {t("video.recent.generatingTitle")}
                      </p>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {pendingItem.label}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {formatRelativeTime(pendingItem.startedAt, i18n.language)}
                    </p>
                    <p className="mt-2 text-xs leading-relaxed text-gray-700">
                      {t("video.recent.generatingHint")}
                    </p>
                  </div>
                </div>
              ))}
              {videos.map(({ content, productId }) => (
                <div
                  key={content.id}
                  className="w-72 flex-shrink-0 overflow-hidden rounded-xl border border-gray-200 bg-white"
                >
                  <button
                    type="button"
                    onClick={() => onPlay(content, productId)}
                    className="group relative block aspect-video w-full overflow-hidden bg-gray-900"
                  >
                    <video
                      src={content.video || ""}
                      muted
                      playsInline
                      preload="metadata"
                      className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                    />
                    {content.generationSettings?.duration && (
                      <span className="absolute bottom-3 left-3 rounded-full bg-black/70 px-3 py-1 text-xs font-semibold text-white">
                        {content.generationSettings.duration}s
                      </span>
                    )}
                  </button>
                  <div className="flex items-end justify-between gap-3 p-4">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {content.imageType || t("video.recent.video")}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {formatRelativeTime(content.timestamp, i18n.language)}
                      </p>
                    </div>
                    {content.fullUrl && (
                      <button
                        type="button"
                        onClick={() => void handleDownload(content)}
                        disabled={downloadingId === content.id}
                        className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
                        aria-label={t("video.recent.download")}
                      >
                        {downloadingId === content.id ? (
                          <span className="block h-[18px] w-[18px] animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                        ) : (
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M12 3v12" />
                            <path d="m7 10 5 5 5-5" />
                            <path d="M5 21h14" />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// How it works placeholder
// ---------------------------------------------------------------------------

interface HowItWorksCardProps {
  presetId: VideoPresetId;
  presetLabel: string;
  hasEndFrameInput: boolean;
  onDismiss: () => void;
}

function HowItWorksCard({
  presetId,
  presetLabel,
  hasEndFrameInput,
  onDismiss,
}: HowItWorksCardProps) {
  const { t } = useTranslation();
  const demoMedia = VIDEO_PRESET_DEMO_MEDIA[presetId];
  const descriptionKeyByPreset: Record<VideoPresetId, string> = {
    "360-packshot": "video.howItWorks.description.360-packshot",
    "product-functionality":
      "video.howItWorks.description.product-functionality",
    "gentle-tilt": "video.howItWorks.description.gentle-tilt",
    "zoom-reveal": "video.howItWorks.description.zoom-reveal",
    "free-prompt": "video.howItWorks.description.free-prompt",
    "bring-to-life": "video.howItWorks.description.bring-to-life",
  };

  const stepMediaClassName = "h-full w-full object-cover";
  const renderStartFrame = (alt: string) =>
    demoMedia.kind === "render" ? (
      demoMedia.howItWorks.start()
    ) : (
      <img
        src={demoMedia.howItWorks.startFrame}
        alt={alt}
        loading="lazy"
        decoding="async"
        className={stepMediaClassName}
      />
    );
  const renderEndFrame = (alt: string) =>
    demoMedia.kind === "render" ? (
      demoMedia.howItWorks.end()
    ) : (
      <img
        src={demoMedia.howItWorks.endFrame}
        alt={alt}
        loading="lazy"
        decoding="async"
        className={stepMediaClassName}
      />
    );
  const renderResult = () =>
    demoMedia.kind === "render" ? (
      demoMedia.howItWorks.result()
    ) : (
      <video
        src={demoMedia.howItWorks.resultVideo}
        poster={demoMedia.howItWorks.startFrame}
        muted
        loop
        autoPlay
        playsInline
        preload="metadata"
        className={stepMediaClassName}
      />
    );

  const howItWorksSteps = [
    {
      title: t("video.howItWorks.step1Title"),
      description: t("video.howItWorks.step1Description"),
      media: renderStartFrame(t("video.howItWorks.step1Title")),
    },
    ...(hasEndFrameInput
      ? [
          {
            title: t("video.howItWorks.step2Title"),
            description: t("video.howItWorks.step2Description"),
            media: renderEndFrame(t("video.howItWorks.step2Title")),
          },
        ]
      : []),
    {
      title: t("video.howItWorks.step3Title"),
      description: t("video.howItWorks.step3Description"),
      media: renderResult(),
    },
  ];

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            {t("video.howItWorks.eyebrow")}
          </p>
          <h2 className="mt-2 text-xl tracking-tight font-semibold text-gray-900">
            {presetLabel}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-500">
            {t(descriptionKeyByPreset[presetId])}
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-gray-300 hover:text-gray-900"
        >
          {t("video.howItWorks.dismiss")}
        </button>
      </div>
      <div className="mt-5 grid gap-5 md:grid-cols-3">
        {howItWorksSteps.map((step, index) => (
          <div key={step.title}>
            <div className="relative mb-3 overflow-hidden rounded-xl border border-gray-200 bg-gray-50 aspect-video">
              {step.media}
              <div className="absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-black/80 text-[11px] font-semibold text-white">
                {index + 1}
              </div>
            </div>
            <h3 className="text-sm font-semibold text-gray-900">
              {step.title}
            </h3>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Main Video tab component
// ---------------------------------------------------------------------------

const VideoStudio: React.FC = () => {
  const { t } = useTranslation();
  const { projectId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    studioProducts,
    setStudioProducts,
    setActiveViewer,
    setStudioImageType,
    setStudioAspectRatio,
  } = useProjectContext();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const updateNotification = useNotificationStore((s) => s.updateNotification);
  const { namingTemplate } = useUserSettings();

  // --- Panel state ---
  const [selectedPresetId, setSelectedPresetId] =
    React.useState<VideoPresetId>("bring-to-life");
  const [sceneType, setSceneType] = React.useState<VideoSceneType>("cutout");
  const [renderSettings, setRenderSettings] =
    React.useState<VideoRenderSettings>({
      aspectRatio: "16:9",
      duration: 4,
      resolution: "720p",
      modelId: "veo-3.1-fast-generate-001",
    });
  const [customPrompt, setCustomPrompt] = React.useState("");
  const [zoomTarget, setZoomTarget] = React.useState("");
  const [bringToLifeMode, setBringToLifeMode] =
    React.useState<BringToLifeMode>("atmosphere");
  const [bringToLifeCustomText, setBringToLifeCustomText] = React.useState("");
  const [bringToLifeCameraMove, setBringToLifeCameraMove] =
    React.useState<BringToLifeCameraMove>("auto");
  const [bringToLifeFocusTarget, setBringToLifeFocusTarget] =
    React.useState("");
  const [bringToLifeAnalysis, setBringToLifeAnalysis] =
    React.useState<VideoSceneAnalysis | null>(null);
  const [bringToLifeAnalysisStoragePath, setBringToLifeAnalysisStoragePath] =
    React.useState<string | null>(null);
  const [isAnalyzingScene, setIsAnalyzingScene] = React.useState(false);
  const [bringToLifeAnalysisError, setBringToLifeAnalysisError] =
    React.useState<string | null>(null);
  // Counter incremented by the retry button to force the analysis effect to
  // re-run against the same start-frame storage path. The path itself is the
  // primary cache key; this is just a tiebreaker so failed attempts can be
  // re-attempted without changing the image.
  const [bringToLifeAnalysisAttempt, setBringToLifeAnalysisAttempt] =
    React.useState(0);
  const [freePromptGuidanceMode, setFreePromptGuidanceMode] =
    React.useState<FreePromptGuidanceMode>("frames");
  const [frameSelections, setFrameSelections] =
    React.useState<VideoFrameSelections>({});
  const [referenceSelections, setReferenceSelections] = React.useState<
    VideoImageSelection[]
  >([]);
  const [pendingVideoResults, setPendingVideoResults] = React.useState<
    Array<{ id: string; label: string; startedAt: number }>
  >([]);
  const [dismissedHowItWorks, setDismissedHowItWorks] = React.useState<
    Partial<Record<VideoPresetId, boolean>>
  >(() => {
    if (typeof window === "undefined") return {};
    try {
      return JSON.parse(
        window.localStorage.getItem(DISMISSED_VIDEO_HOW_IT_WORKS_STORAGE_KEY) ??
          "{}"
      );
    } catch {
      return {};
    }
  });
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] =
    React.useState(false);
  const [uploadingFrameRole, setUploadingFrameRole] =
    React.useState<VideoFrameRole | null>(null);
  const [isUploadingReferenceImage, setIsUploadingReferenceImage] =
    React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [pickingImageTarget, setPickingImageTarget] =
    React.useState<VideoImagePickTarget | null>(null);

  const selectedPreset = getVideoPreset(selectedPresetId);
  const selectedPresetLabel = getPresetLabel(t, selectedPreset);
  const selectedPresetDescription = getPresetDescription(t, selectedPreset);
  const selectedModelOption = getVideoModelOption(renderSettings.modelId);
  const isCustomPromptMode = selectedPreset.promptMode === "custom";
  const usesReferenceImages =
    isCustomPromptMode && freePromptGuidanceMode === "references";
  const showHowItWorks =
    selectedPresetId !== "free-prompt" &&
    !dismissedHowItWorks[selectedPresetId];
  const frameInputs = React.useMemo(
    () => (usesReferenceImages ? [] : selectedPreset.frameInputs),
    [selectedPreset, usesReferenceImages]
  );
  const supportsEndFrame = frameInputs.some((input) => input.role === "end");
  const startFrame = frameSelections.start;
  const endFrame = supportsEndFrame ? frameSelections.end : undefined;
  const compatibleDurations = getCompatibleDurations(
    selectedPreset,
    selectedModelOption,
    renderSettings
  );
  const allowedDurations = usesReferenceImages
    ? compatibleDurations.filter((duration) => duration === 8)
    : compatibleDurations;
  const allowedResolutions = getCompatibleResolutions(selectedModelOption);
  const generationCreditCost = getVideoGenerationCreditCost(renderSettings);

  React.useEffect(() => {
    if (!usesReferenceImages || renderSettings.duration === 8) return;
    setRenderSettings((prev) =>
      prev.duration === 8 ? prev : { ...prev, duration: 8 }
    );
  }, [renderSettings.duration, usesReferenceImages]);

  // Bring to Life: clear cached analysis when the preset is left.
  React.useEffect(() => {
    if (selectedPresetId !== "bring-to-life") {
      setBringToLifeAnalysis(null);
      setBringToLifeAnalysisStoragePath(null);
      setBringToLifeAnalysisError(null);
    }
  }, [selectedPresetId]);

  // Bring to Life: run scene analysis when the start frame changes. Reads
  // from the StudioImage cache when available; otherwise calls the backend
  // and persists the result back so future sessions hit the cache.
  // Caching only works for generated images (those have a real StudioImage
  // row) — uploaded product images live on StudioProduct columns and have a
  // client-only id, so we skip the persist call to avoid silent 0-row writes.
  const startFrameStoragePath = startFrame?.image.storagePath;
  const startFrameId = startFrame?.image.id;
  const cachedStudioImage = React.useMemo(() => {
    if (!startFrameId) return undefined;
    return studioProducts
      .flatMap((product) => product.generatedContent)
      .find((content) => content.id === startFrameId);
  }, [studioProducts, startFrameId]);
  const isPersistedStudioImage = !!cachedStudioImage;
  React.useEffect(() => {
    if (selectedPresetId !== "bring-to-life") return;
    if (!startFrameStoragePath || !startFrameStoragePath.startsWith("f/"))
      return;
    if (bringToLifeAnalysisStoragePath === startFrameStoragePath) return;

    const cached = cachedStudioImage?.videoAnalysis;

    if (cached) {
      setBringToLifeAnalysis(cached);
      setBringToLifeAnalysisStoragePath(startFrameStoragePath);
      setBringToLifeAnalysisError(null);
      setIsAnalyzingScene(false);
      return;
    }

    let cancelled = false;
    setIsAnalyzingScene(true);
    setBringToLifeAnalysisError(null);
    (async () => {
      try {
        const analysis = await analyzeVideoScene(startFrame!.image);
        if (cancelled) return;
        setBringToLifeAnalysis(analysis);
        setBringToLifeAnalysisStoragePath(startFrameStoragePath);
        if (startFrameId && isPersistedStudioImage) {
          persistVideoAnalysis(startFrameId, analysis).catch((error) => {
            reportError(error, {
              context: "persistVideoAnalysis",
              imageId: startFrameId,
            });
          });
        }
      } catch (error) {
        if (cancelled) return;
        setBringToLifeAnalysis(null);
        setBringToLifeAnalysisStoragePath(null);
        setBringToLifeAnalysisError(resolveErrorMessage(error, t));
      } finally {
        if (!cancelled) setIsAnalyzingScene(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedPresetId,
    startFrameStoragePath,
    startFrameId,
    bringToLifeAnalysisStoragePath,
    bringToLifeAnalysisAttempt,
    cachedStudioImage,
    isPersistedStudioImage,
    startFrame,
    t,
  ]);

  // Bump the attempt counter so the analysis effect re-runs against the same
  // start-frame storage path. After a failure storagePath is already null and
  // setting it to null again is a no-op for React, which would prevent the
  // effect from firing — the counter is the dep that always changes on retry.
  const handleRetryBringToLifeAnalysis = React.useCallback(() => {
    setBringToLifeAnalysisError(null);
    setBringToLifeAnalysisAttempt((n) => n + 1);
  }, []);

  // Bring to Life: when the user picks Lifestyle Moment but the analysis
  // says no person is present, fall back to Atmosphere so the Generate
  // button can stay enabled. The button itself is gated separately.
  React.useEffect(() => {
    if (selectedPresetId !== "bring-to-life") return;
    if (
      bringToLifeMode === "lifestyle-moment" &&
      bringToLifeAnalysis &&
      bringToLifeAnalysis.hasPeople === false
    ) {
      setBringToLifeMode("atmosphere");
    }
  }, [selectedPresetId, bringToLifeMode, bringToLifeAnalysis]);

  const trimmedCustomPrompt = customPrompt.trim();
  const trimmedZoomTarget = zoomTarget.trim();
  const hasStartFramePath = Boolean(
    startFrame?.image.storagePath?.startsWith("f/")
  );
  const hasEndFramePath = Boolean(
    endFrame?.image.storagePath?.startsWith("f/")
  );
  const endFrameRequiresStart = isCustomPromptMode && hasEndFramePath;
  const hasValidEndFrameSetup = !endFrameRequiresStart || hasStartFramePath;
  const mismatchedFrames = React.useMemo(() => {
    if (usesReferenceImages) return [];
    return (
      Object.entries(frameSelections) as Array<
        [VideoFrameRole, VideoFrameSelections[VideoFrameRole]]
      >
    )
      .filter(([, selection]) => selection?.image)
      .filter(
        ([, selection]) =>
          !doesImageMatchAspectRatio(
            selection!.image,
            renderSettings.aspectRatio
          )
      );
  }, [frameSelections, renderSettings.aspectRatio, usesReferenceImages]);
  const hasAspectRatioMismatch = mismatchedFrames.length > 0;
  const aspectRatioMismatchFrames = mismatchedFrames
    .map(([role]) => t(`video.frame.${role}`))
    .join(", ");
  const referenceStoragePaths = referenceSelections
    .map((selection) => selection.image.storagePath)
    .filter((path): path is string => Boolean(path?.startsWith("f/")));
  const hasValidReferenceImageSetup =
    !usesReferenceImages ||
    (referenceSelections.length > 0 &&
      referenceStoragePaths.length === referenceSelections.length);

  const handleOpenAspectRatioTool = () => {
    const productId = mismatchedFrames.find(
      ([, selection]) => selection?.productId
    )?.[1]?.productId;

    setStudioImageType("Aspect Ratio");
    setStudioAspectRatio(renderSettings.aspectRatio);
    if (productId) {
      setStudioProducts((prev) =>
        prev.map((product) => ({
          ...product,
          isSelected: product.id === productId,
        }))
      );
    }
    navigate({ to: "/projects/$projectId", params: { projectId } });
  };

  React.useEffect(() => {
    const supportedRoles = new Set(frameInputs.map((input) => input.role));
    setFrameSelections((prev) => {
      let changed = false;
      const next: VideoFrameSelections = {};
      for (const [role, selection] of Object.entries(prev) as Array<
        [VideoFrameRole, VideoFrameSelections[VideoFrameRole]]
      >) {
        if (supportedRoles.has(role)) {
          next[role] = selection;
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [frameInputs]);

  // When preset changes, reset scene type and clamp duration to allowed values
  // Video quick start (SIS-566): one prompt asks for a preset; the click
  // ends the tour and puts a spotlight on that preset's "How it works",
  // which carries the user from there. Nothing else is guided.
  const {
    activeFlow: quickStartFlow,
    complete: completeQuickStart,
    skip: skipQuickStart,
  } = useQuickStartTour();
  const isVideoTourActive = quickStartFlow === "video";
  const [howItWorksSpotlight, setHowItWorksSpotlight] = React.useState(false);
  const presetStageEmittedRef = React.useRef(false);
  React.useEffect(() => {
    if (!isVideoTourActive || presetStageEmittedRef.current) return;
    presetStageEmittedRef.current = true;
    trackQuickstartStageReached("preset", "video");
  }, [isVideoTourActive]);
  const skipVideoTour = () => {
    trackQuickstartSkipped("preset", "video");
    skipQuickStart();
  };

  const handleSelectPreset = (presetId: VideoPresetId) => {
    // Free Prompt has no "How it works" card or pill, so there is nothing
    // to hand over to; the tour still completes.
    const hasGuide = presetId !== "free-prompt";
    if (isVideoTourActive) {
      if (hasGuide) trackQuickstartStageReached("how_it_works", "video");
      trackQuickstartCompleted("video");
      completeQuickStart();
      setHowItWorksSpotlight(hasGuide);
    } else if (!hasGuide) {
      setHowItWorksSpotlight(false);
    }
    const preset = getVideoPreset(presetId);
    setSelectedPresetId(presetId);
    if (!preset.supportedSceneTypes.includes(sceneType)) {
      setSceneType(preset.supportedSceneTypes[0]);
    }
    setRenderSettings((prev) =>
      clampRenderSettingsForPresetAndModel(preset, prev)
    );
  };

  // All video results across all products, newest first
  const videoResults = React.useMemo(() => {
    const items: Array<{
      content: GeneratedContent;
      productId: string;
    }> = [];
    for (const product of studioProducts) {
      for (const content of product.generatedContent) {
        if (content.video) {
          items.push({ content, productId: product.id });
        }
      }
    }
    items.sort(
      (a, b) => (b.content.timestamp ?? 0) - (a.content.timestamp ?? 0)
    );
    return items;
  }, [studioProducts]);

  // --- Handlers ---

  const createVideoStudioTile = async (options: {
    sourceImage?: AppImageFile;
    title: string;
    description?: string;
  }): Promise<StudioProduct> => {
    if (!auth.currentUser) {
      throw new Error(t("video.notification.authRequired"));
    }

    const productId = crypto.randomUUID();
    const title = getVideoTileTitle(options.title);
    const rawSourceName =
      options.sourceImage?.name ||
      `${sanitizeVideoTileFilename(title.toLowerCase().replace(/\s+/g, "-"))}.png`;
    const sourceName = sanitizeVideoTileFilename(rawSourceName);
    const mimeType = options.sourceImage?.mimeType || "image/png";
    const path = `f/${auth.currentUser.uid}/${projectId}/${productId}/original_${sourceName}`;
    const blob = options.sourceImage
      ? await imageFileToBlob(options.sourceImage)
      : await createPromptOnlyVideoCoverBlob(title, renderSettings.aspectRatio);
    const coverImage = await uploadAndResolve(
      blob,
      path,
      blob.type || mimeType,
      sourceName
    );

    return {
      id: productId,
      image: coverImage,
      originalImage: coverImage,
      info: "video",
      itemCount: null,
      isSelected: false,
      isProcessing: false,
      generatedContent: [],
      isGenerating: true,
      error: null,
      productCategory: "Video",
      productDescription: options.description || title,
      analysisStatus: "success",
      analysisError: null,
    };
  };

  const handlePickImage = (image: AppImageFile, productId: string) => {
    if (!pickingImageTarget) return;
    if (pickingImageTarget === "reference") {
      setReferenceSelections((prev) => {
        if (prev.length >= MAX_VIDEO_REFERENCE_IMAGES) return prev;
        return [...prev, { image, productId }];
      });
      setPickingImageTarget(null);
      return;
    }

    setFrameSelections((prev) => ({
      ...prev,
      [pickingImageTarget]: { image, productId },
    }));
    setPickingImageTarget(null);
  };

  const handleUploadFrame = async (role: VideoFrameRole, file: File) => {
    if (uploadingFrameRole) return;
    if (!auth.currentUser) {
      addNotification({
        type: "error",
        projectId,
        title: t("video.notification.authRequired"),
      });
      return;
    }

    if (file.size > MAX_VIDEO_FRAME_FILE_SIZE_BYTES) {
      addNotification({
        type: "error",
        projectId,
        title: t("video.notification.uploadTooLarge"),
      });
      return;
    }

    // Sniff the bytes: the input's `accept` list does not stop a mislabeled or
    // type-less file, and a non-image frame only fails much later inside the
    // backend's sharp pipeline (IMAGE-STUDIO-4Z).
    const frameMimeType = await detectImageMimeType(file);
    if (!frameMimeType) {
      addNotification({
        type: "error",
        projectId,
        title: t("video.notification.uploadUnsupportedType"),
      });
      return;
    }

    setUploadingFrameRole(role);
    try {
      if (role === "start") {
        const product = await uploadFileAsProduct(
          file,
          projectId,
          auth.currentUser.uid
        );
        await persistStudioProduct(projectId, product);
        setStudioProducts((prev) => [product, ...prev]);
        setFrameSelections((prev) => ({
          ...prev,
          start: { image: product.image, productId: product.id },
        }));
        return;
      }

      const frameId = crypto.randomUUID();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path =
        `f/${auth.currentUser.uid}/${projectId}/video-frames/` +
        `${frameId}_${safeName}`;
      const image = await uploadAndResolve(
        file,
        path,
        frameMimeType,
        file.name
      );
      setFrameSelections((prev) => ({
        ...prev,
        [role]: { image, productId: null },
      }));
    } catch (err) {
      reportError(err, {
        context: "video_frame_upload",
        projectId,
        role,
        fileName: sanitizeFileNameForReport(file.name),
        fileSize: file.size,
        fileType: file.type,
      });
      addNotification({
        type: "error",
        projectId,
        title: t("video.notification.uploadFailed"),
        message: resolveErrorMessage(err, t),
      });
    } finally {
      setUploadingFrameRole(null);
    }
  };

  const handleClearFrame = (role: VideoFrameRole) => {
    setFrameSelections((prev) => {
      const next = { ...prev };
      delete next[role];
      return next;
    });
  };

  const handleUploadReferenceImage = async (file: File) => {
    if (isUploadingReferenceImage) return;
    if (referenceSelections.length >= MAX_VIDEO_REFERENCE_IMAGES) return;
    if (!auth.currentUser) {
      addNotification({
        type: "error",
        projectId,
        title: t("video.notification.authRequired"),
      });
      return;
    }

    if (file.size > MAX_VIDEO_FRAME_FILE_SIZE_BYTES) {
      addNotification({
        type: "error",
        projectId,
        title: t("video.notification.uploadTooLarge"),
      });
      return;
    }

    const referenceMimeType = await detectImageMimeType(file);
    if (!referenceMimeType) {
      addNotification({
        type: "error",
        projectId,
        title: t("video.notification.uploadUnsupportedType"),
      });
      return;
    }

    setIsUploadingReferenceImage(true);
    try {
      const referenceId = crypto.randomUUID();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path =
        `f/${auth.currentUser.uid}/${projectId}/video-references/` +
        `${referenceId}_${safeName}`;
      const image = await uploadAndResolve(
        file,
        path,
        referenceMimeType,
        file.name
      );
      setReferenceSelections((prev) => [...prev, { image, productId: null }]);
    } catch (err) {
      reportError(err, {
        context: "video_reference_upload",
        projectId,
        fileName: sanitizeFileNameForReport(file.name),
        fileSize: file.size,
        fileType: file.type,
      });
      addNotification({
        type: "error",
        projectId,
        title: t("video.notification.uploadFailed"),
        message: resolveErrorMessage(err, t),
      });
    } finally {
      setIsUploadingReferenceImage(false);
    }
  };

  const handleRemoveReferenceImage = (index: number) => {
    setReferenceSelections((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGenerate = async () => {
    if (!canGenerate) {
      return;
    }

    setIsGenerating(true);
    let createdVideoTile: StudioProduct | null = null;
    let notificationId: string | null = null;
    const pendingResultId = crypto.randomUUID();
    setPendingVideoResults((prev) => [
      {
        id: pendingResultId,
        label: selectedPresetLabel,
        startedAt: Date.now(),
      },
      ...prev,
    ]);

    try {
      let anchorProductId =
        (!usesReferenceImages
          ? (startFrame?.productId ?? endFrame?.productId)
          : null) ?? null;

      if (usesReferenceImages || !anchorProductId) {
        const tileTitle = usesReferenceImages
          ? t("video.panel.referenceImages")
          : trimmedCustomPrompt;
        createdVideoTile = await createVideoStudioTile({
          sourceImage: usesReferenceImages
            ? referenceSelections[0]?.image
            : undefined,
          title: tileTitle,
          description: trimmedCustomPrompt || selectedPresetDescription,
        });
        await persistStudioProduct(projectId, createdVideoTile);
        anchorProductId = createdVideoTile.id;
        setStudioProducts((prev) => [createdVideoTile!, ...prev]);
      }

      notificationId = addNotification({
        type: "progress",
        projectId,
        title: t("video.notification.generating"),
        message: t("video.notification.generatingHint"),
        imageType: selectedPresetLabel,
        productId: anchorProductId ?? undefined,
      });

      const sourceName =
        startFrame?.image.name ||
        (usesReferenceImages
          ? referenceSelections[0]?.image.name || "reference"
          : "prompt");
      const userDetail = isCustomPromptMode
        ? trimmedCustomPrompt
        : selectedPresetId === "zoom-reveal"
          ? trimmedZoomTarget
          : "";
      const anchorProduct = studioProducts.find(
        (p) => p.id === anchorProductId
      );
      const existingTitles = anchorProduct
        ? anchorProduct.generatedContent.map((c) => c.title)
        : [];
      // Named before the call — the backend writes the DB row from the
      // settings this request carries, so a title set on the result afterwards
      // never reaches it and the video reloads as "Generated Video"
      // (SIS-517).
      const title = generateUniqueFilename(
        namingTemplate,
        {
          originalName: sourceName,
          type: selectedPresetLabel,
          scene: t(`video.sceneType.${sceneType}`, { defaultValue: sceneType }),
          details: userDetail.slice(0, 60),
        },
        existingTitles,
        "mp4"
      );

      const result = await generateVideo({
        preset: selectedPresetId,
        sceneType,
        storagePath: usesReferenceImages
          ? undefined
          : startFrame?.image.storagePath,
        endFrameStoragePath: usesReferenceImages
          ? undefined
          : endFrame?.image.storagePath,
        referenceStoragePaths: usesReferenceImages
          ? referenceStoragePaths
          : undefined,
        customPrompt: isCustomPromptMode ? trimmedCustomPrompt : undefined,
        zoomTarget:
          selectedPresetId === "zoom-reveal" && trimmedZoomTarget.length > 0
            ? trimmedZoomTarget
            : undefined,
        bringToLifeMode: isBringToLife ? bringToLifeMode : undefined,
        bringToLifeAnalysis:
          isBringToLife && bringToLifeAnalysis
            ? bringToLifeAnalysis
            : undefined,
        bringToLifeCustomText:
          isBringToLife && trimmedBringToLifeCustom.length > 0
            ? trimmedBringToLifeCustom
            : undefined,
        bringToLifeCameraMove:
          isBringToLife && bringToLifeMode === "camera-movement"
            ? bringToLifeCameraMove
            : undefined,
        bringToLifeFocusTarget:
          isBringToLife &&
          bringToLifeMode === "camera-movement" &&
          bringToLifeFocusTarget.trim().length > 0
            ? bringToLifeFocusTarget.trim()
            : undefined,
        renderSettings,
        projectId,
        productId: anchorProductId ?? undefined,
        title,
      });

      const resultWithTitle = {
        ...result,
        title,
      };

      setStudioProducts((prev) =>
        prev.map((p) => {
          if (p.id !== anchorProductId) return p;
          return {
            ...p,
            generatedContent: [resultWithTitle, ...p.generatedContent],
            isGenerating: false,
            error: null,
          };
        })
      );

      void queryClient.invalidateQueries({ queryKey: ["GetUserCredits"] });

      updateNotification(notificationId, {
        type: "success",
        title: t("video.notification.success"),
        message: selectedPresetLabel,
        contentId: resultWithTitle.id,
        thumbnailUrl:
          resultWithTitle.thumbnailUrl || resultWithTitle.image || undefined,
        resultCount: 1,
      });
    } catch (err) {
      if (createdVideoTile) {
        setStudioProducts((prev) =>
          prev.filter((product) => product.id !== createdVideoTile?.id)
        );
        deleteStudioProduct(projectId, createdVideoTile).catch((deleteErr) => {
          console.error("Failed to clean up failed video tile", deleteErr);
        });
      }
      if (notificationId) {
        updateNotification(notificationId, {
          type: "error",
          message: resolveErrorMessage(err, t),
        });
      } else {
        addNotification({
          type: "error",
          projectId,
          title: t("video.notification.uploadFailed"),
          message: resolveErrorMessage(err, t),
        });
      }
    } finally {
      setPendingVideoResults((prev) =>
        prev.filter((item) => item.id !== pendingResultId)
      );
      setIsGenerating(false);
    }
  };

  const handleViewVideo = (content: GeneratedContent, productId: string) => {
    setActiveViewer({
      content,
      productId,
      currentContentId: content.id,
    });
  };

  const isBringToLife = selectedPresetId === "bring-to-life";
  const trimmedBringToLifeCustom = bringToLifeCustomText.trim();
  const isLifestyleModeBlocked =
    isBringToLife &&
    bringToLifeMode === "lifestyle-moment" &&
    bringToLifeAnalysis !== null &&
    bringToLifeAnalysis.hasPeople === false;
  const isBringToLifeReady =
    !isBringToLife ||
    (bringToLifeAnalysis !== null &&
      !isAnalyzingScene &&
      !bringToLifeAnalysisError &&
      !isLifestyleModeBlocked &&
      bringToLifeCustomText.length <= MAX_BRING_TO_LIFE_CUSTOM_TEXT_LENGTH);

  const canGenerate =
    (!isCustomPromptMode ||
      (trimmedCustomPrompt.length > 0 &&
        customPrompt.length <= MAX_CUSTOM_VIDEO_PROMPT_LENGTH)) &&
    hasValidReferenceImageSetup &&
    hasValidEndFrameSetup &&
    (usesReferenceImages ||
      frameInputs
        .filter((input) => input.required)
        .every((input) =>
          Boolean(
            frameSelections[input.role]?.image.storagePath?.startsWith("f/")
          )
        )) &&
    isBringToLifeReady &&
    !isGenerating &&
    !uploadingFrameRole &&
    !isUploadingReferenceImage;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex-grow h-full bg-gray-50/50 flex overflow-hidden">
      {/* Left sidebar */}
      <aside className="w-[400px] shrink-0 bg-white border-r border-gray-200 flex flex-col min-h-0">
        <div className="flex-grow overflow-y-auto p-6 space-y-5">
          {/* Presets */}
          {isVideoTourActive && (
            <QuickStartPrompt
              eyebrow={t("quickStart.studioEyebrow")}
              title={t("quickStart.video.presetTitle")}
              description={t("quickStart.video.presetDesc")}
              onSkip={skipVideoTour}
            />
          )}
          <ControlSection
            title={t("video.panel.preset")}
            highlighted={isVideoTourActive}
          >
            <div className="space-y-2">
              {VIDEO_PRESETS.filter((preset) => !preset.hidden).map(
                (preset) => (
                  <PresetCard
                    key={preset.id}
                    preset={preset}
                    selected={selectedPresetId === preset.id}
                    onSelect={() => handleSelectPreset(preset.id)}
                  />
                )
              )}
            </div>
            <p className="mt-3 text-xs text-gray-500">
              {t("video.panel.presetSuggestionNote")}
            </p>
          </ControlSection>

          {/* Model */}
          <ControlSection title={t("video.panel.model")}>
            <div className="flex bg-gray-100 p-1 rounded-lg">
              {VIDEO_MODEL_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  onClick={() =>
                    setRenderSettings((prev) =>
                      clampRenderSettingsForPresetAndModel(selectedPreset, {
                        ...prev,
                        modelId: option.id,
                      })
                    )
                  }
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-lg transition-colors ${
                    renderSettings.modelId === option.id
                      ? "bg-white text-black shadow-sm"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
                  }`}
                >
                  <option.Icon className="h-3 w-3" />
                  {t(option.labelKey)}
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-gray-500">
              {t(selectedModelOption.hintKey)}
            </p>
          </ControlSection>

          {/* Scene type toggle — only for presets that need it */}
          {selectedPreset.sceneTypePicker && (
            <ControlSection title={t("video.panel.sceneType")}>
              <div className="flex bg-gray-100 p-1 rounded-lg">
                {selectedPreset.supportedSceneTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => setSceneType(type)}
                    className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${
                      sceneType === type
                        ? "bg-white text-black shadow-sm"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
                    }`}
                  >
                    {t(`video.sceneType.${type}`)}
                  </button>
                ))}
              </div>
            </ControlSection>
          )}

          {/* Aspect ratio */}
          <ControlSection title={t("video.panel.aspectRatio")}>
            <div className="flex bg-gray-100 p-1 rounded-lg">
              {(["16:9", "9:16"] as const).map((ratio) => (
                <button
                  key={ratio}
                  onClick={() =>
                    setRenderSettings((prev) => ({
                      ...prev,
                      aspectRatio: ratio,
                    }))
                  }
                  className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${
                    renderSettings.aspectRatio === ratio
                      ? "bg-white text-black shadow-sm"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
                  }`}
                >
                  {ratio}
                </button>
              ))}
            </div>
          </ControlSection>

          {/* Duration */}
          <ControlSection title={t("video.panel.duration")}>
            <div className="flex bg-gray-100 p-1 rounded-lg">
              {VIDEO_DURATION_OPTIONS.map((sec) => {
                const allowed = allowedDurations.includes(sec);
                return (
                  <button
                    key={sec}
                    onClick={() =>
                      allowed &&
                      setRenderSettings((prev) => ({ ...prev, duration: sec }))
                    }
                    disabled={!allowed}
                    className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${
                      !allowed
                        ? "text-gray-400 cursor-not-allowed"
                        : renderSettings.duration === sec
                          ? "bg-white text-black shadow-sm"
                          : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
                    }`}
                  >
                    {sec}s
                  </button>
                );
              })}
            </div>
          </ControlSection>

          {/* Resolution */}
          <ControlSection title={t("video.panel.resolution")}>
            <div className="flex bg-gray-100 p-1 rounded-lg">
              {VIDEO_RESOLUTION_OPTIONS.map((resolution) => {
                const allowed = allowedResolutions.includes(resolution);
                return (
                  <button
                    key={resolution}
                    onClick={() =>
                      allowed &&
                      setRenderSettings((prev) => ({
                        ...prev,
                        resolution,
                      }))
                    }
                    disabled={!allowed}
                    className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${
                      !allowed
                        ? "text-gray-400 cursor-not-allowed"
                        : renderSettings.resolution === resolution
                          ? "bg-white text-black shadow-sm"
                          : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
                    }`}
                  >
                    {resolution}
                  </button>
                );
              })}
            </div>
          </ControlSection>
        </div>
        <div className="border-t border-gray-200 bg-white p-4">
          <button
            onClick={handleGenerate}
            disabled={!canGenerate}
            className={`w-full rounded-lg px-5 py-2.5 text-sm font-semibold transition-all flex items-center justify-between gap-2 ${
              canGenerate
                ? "bg-black text-white hover:bg-gray-800 active:scale-95"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            <span>
              {isGenerating
                ? t("video.panel.generating")
                : t("video.panel.generate")}
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] font-mono">
              <CreditIcon className="h-3 w-3" />
              {formatCreditAmount(generationCreditCost)}
            </span>
          </button>
        </div>
      </aside>

      {/* Content area */}
      <main className="flex-grow overflow-y-auto p-6">
        <div className="mx-auto max-w-6xl space-y-6">
          {howItWorksSpotlight && (
            <QuickStartPrompt
              eyebrow={t("quickStart.studioEyebrow")}
              title={t("quickStart.video.howItWorksTitle")}
              // The spotlight lands on the card, or — once this preset's
              // card has been dismissed (localStorage, so it survives into
              // the tour) — on the pill that reopens it. Exactly one of the
              // two is on screen, and the wording has to name the one that
              // actually lit up.
              description={t(
                showHowItWorks
                  ? "quickStart.video.howItWorksDesc"
                  : "quickStart.video.howItWorksPillDesc"
              )}
              actionLabel={t("quickStart.video.howItWorksDone")}
              onSkip={() => setHowItWorksSpotlight(false)}
            />
          )}
          {showHowItWorks && (
            <div
              className={
                howItWorksSpotlight
                  ? "rounded-xl ring-4 ring-blue-500/25"
                  : undefined
              }
            >
              <HowItWorksCard
                presetId={selectedPresetId}
                presetLabel={selectedPresetLabel}
                hasEndFrameInput={supportsEndFrame}
                onDismiss={() => {
                  setHowItWorksSpotlight(false);
                  setDismissedHowItWorks((prev) => {
                    const next = { ...prev, [selectedPresetId]: true };
                    try {
                      window.localStorage.setItem(
                        DISMISSED_VIDEO_HOW_IT_WORKS_STORAGE_KEY,
                        JSON.stringify(next)
                      );
                    } catch {
                      // Ignore storage failures; the current session still hides it.
                    }
                    return next;
                  });
                }}
              />
            </div>
          )}

          <section className="rounded-xl border border-gray-200 bg-white p-6">
            <div className="mb-5">
              {!showHowItWorks && (
                <div className="flex items-center gap-2">
                  <h2 className="text-xl tracking-tight font-semibold text-gray-900">
                    {selectedPresetLabel}
                  </h2>
                  {selectedPresetId !== "free-prompt" && (
                    <button
                      type="button"
                      onClick={() => {
                        setHowItWorksSpotlight(false);
                        setIsHowItWorksModalOpen(true);
                      }}
                      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700 transition hover:border-gray-300 hover:text-gray-900 ${
                        howItWorksSpotlight
                          ? "border-blue-400 ring-4 ring-blue-500/25"
                          : "border-gray-200"
                      }`}
                    >
                      {t("video.howItWorks.eyebrow")}
                    </button>
                  )}
                </div>
              )}
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-gray-500">
                {isCustomPromptMode
                  ? t("video.input.customDescription")
                  : selectedPresetDescription}
              </p>
            </div>

            {/* Step 1 (free prompt): pick guidance mode — controls which
                inputs render below. Must come before the image picker so the
                user knows what kind of input they are providing. */}
            {isCustomPromptMode && (
              <div className="mb-5">
                <p className="mb-2 block text-sm font-semibold text-gray-900">
                  {t("video.panel.guidanceMode")}
                </p>
                <div className="flex bg-gray-100 p-1 rounded-lg">
                  {(["frames", "references"] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => {
                        setFreePromptGuidanceMode(mode);
                        setRenderSettings((prev) =>
                          mode === "references"
                            ? {
                                ...clampRenderSettingsForPresetAndModel(
                                  selectedPreset,
                                  prev
                                ),
                                duration: 8,
                              }
                            : clampRenderSettingsForPresetAndModel(
                                selectedPreset,
                                prev
                              )
                        );
                      }}
                      className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${
                        freePromptGuidanceMode === mode
                          ? "bg-white text-black shadow-sm"
                          : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
                      }`}
                    >
                      {t(`video.guidanceMode.${mode}`)}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs leading-relaxed text-gray-500">
                  {usesReferenceImages
                    ? t("video.panel.referenceModeHint", {
                        count: MAX_VIDEO_REFERENCE_IMAGES,
                      })
                    : t("video.panel.frameModeHint")}
                </p>
              </div>
            )}

            {/* Step (all presets): pick the source image(s). Always rendered
                first so users select an image before configuring how it
                should be animated. */}
            {isBringToLife && (
              <p className="mb-2 inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                <span className="rounded-full bg-gray-900 px-1.5 py-0.5 text-[9px] text-white">
                  1
                </span>
                {t("video.bringToLife.step1Label")}
              </p>
            )}
            {usesReferenceImages ? (
              <VideoReferenceImagesCard
                selections={referenceSelections}
                isUploading={isUploadingReferenceImage}
                onPickLibrary={() => setPickingImageTarget("reference")}
                onUpload={handleUploadReferenceImage}
                onRemove={handleRemoveReferenceImage}
              />
            ) : (
              <div
                className={
                  frameInputs.length > 1
                    ? "grid gap-5 lg:grid-cols-2"
                    : "grid gap-5"
                }
              >
                {frameInputs.map((frameInput) => (
                  <VideoFrameInputCard
                    key={frameInput.role}
                    frameInput={frameInput}
                    image={frameSelections[frameInput.role]?.image ?? null}
                    isUploading={uploadingFrameRole === frameInput.role}
                    onPickLibrary={() => setPickingImageTarget(frameInput.role)}
                    onUpload={(file) =>
                      handleUploadFrame(frameInput.role, file)
                    }
                    onClear={() => handleClearFrame(frameInput.role)}
                  />
                ))}
              </div>
            )}
            {isBringToLife && hasStartFramePath && (
              <SceneAnalysisIndicator
                isAnalyzing={isAnalyzingScene}
                hasAnalysis={!!bringToLifeAnalysis}
                errorMessage={bringToLifeAnalysisError}
                onRetry={handleRetryBringToLifeAnalysis}
              />
            )}

            {/* Free prompt: textarea below the image picker so users describe
                the video after they've chosen the inputs that guide it. */}
            {isCustomPromptMode && (
              <div className="mt-5">
                <label className="mb-2 block text-sm font-semibold text-gray-900">
                  {t("video.panel.prompt")}
                </label>
                <textarea
                  value={customPrompt}
                  onChange={(event) => setCustomPrompt(event.target.value)}
                  maxLength={MAX_CUSTOM_VIDEO_PROMPT_LENGTH}
                  rows={5}
                  placeholder={t("video.panel.promptPlaceholder")}
                  className="w-full resize-none rounded-lg border border-gray-300 bg-white p-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                />
                <div className="mt-2 flex items-center justify-between gap-3 text-[10px] text-gray-500">
                  <span>{t("video.panel.promptHint")}</span>
                  <span>
                    {customPrompt.length}/{MAX_CUSTOM_VIDEO_PROMPT_LENGTH}
                  </span>
                </div>
              </div>
            )}

            {/* Zoom Reveal: target detail to end on. Comes after the start
                frame so the user knows what scene they are pointing at. */}
            {selectedPresetId === "zoom-reveal" && (
              <div className="mt-5">
                <label className="mb-2 block text-sm font-semibold text-gray-900">
                  {t("video.panel.zoomTarget")}
                </label>
                <input
                  type="text"
                  value={zoomTarget}
                  onChange={(event) => setZoomTarget(event.target.value)}
                  maxLength={MAX_ZOOM_TARGET_LENGTH}
                  placeholder={t("video.panel.zoomTargetPlaceholder")}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                />
                <div className="mt-2 flex items-center justify-between gap-3 text-[10px] text-gray-500">
                  <span>{t("video.panel.zoomTargetHint")}</span>
                  <span>
                    {zoomTarget.length}/{MAX_ZOOM_TARGET_LENGTH}
                  </span>
                </div>
              </div>
            )}

            {/* Bring to Life: animation style + analysis result. Comes after
                the source image so the dependency is visually clear. */}
            {isBringToLife && (
              <div className="mt-5">
                <BringToLifeControls
                  mode={bringToLifeMode}
                  onModeChange={setBringToLifeMode}
                  customText={bringToLifeCustomText}
                  onCustomTextChange={setBringToLifeCustomText}
                  cameraMove={bringToLifeCameraMove}
                  onCameraMoveChange={setBringToLifeCameraMove}
                  focusTarget={bringToLifeFocusTarget}
                  onFocusTargetChange={setBringToLifeFocusTarget}
                  analysis={bringToLifeAnalysis}
                  isAnalyzing={isAnalyzingScene}
                  analysisError={bringToLifeAnalysisError}
                  hasStartFrame={hasStartFramePath}
                  isLifestyleBlocked={isLifestyleModeBlocked}
                />
              </div>
            )}
            <div
              className={`overflow-hidden transition-all duration-200 ease-out ${
                hasAspectRatioMismatch
                  ? "mt-0 max-h-0 opacity-0"
                  : "mt-3 max-h-20 opacity-100"
              }`}
              aria-hidden={hasAspectRatioMismatch}
            >
              <p className="w-full rounded-lg bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-700">
                {t("video.input.aspectRatioNote", {
                  ratio: renderSettings.aspectRatio,
                })}
              </p>
            </div>
            {hasAspectRatioMismatch && (
              <div className="mt-4 flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex gap-3">
                  <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                  <div>
                    <p className="font-semibold">
                      {t("video.input.aspectRatioWarningTitle")}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-amber-800">
                      {t("video.input.aspectRatioWarningBody", {
                        frames: aspectRatioMismatchFrames,
                        ratio: renderSettings.aspectRatio,
                      })}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleOpenAspectRatioTool}
                  className="shrink-0 rounded-lg bg-amber-900 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-800"
                >
                  {t("video.input.openAspectRatioTool")}
                </button>
              </div>
            )}

            {endFrameRequiresStart && !hasStartFramePath && (
              <p className="mt-4 text-xs text-amber-700">
                {t("video.panel.endFrameRequiresStart")}
              </p>
            )}
          </section>

          <RecentVideoStrip
            videos={videoResults}
            pendingItems={pendingVideoResults}
            onPlay={handleViewVideo}
          />
        </div>
      </main>

      {/* Library picker modal */}
      {pickingImageTarget && (
        <LibraryPickerModal
          targetAspectRatio={renderSettings.aspectRatio}
          onClose={() => setPickingImageTarget(null)}
          onSelect={handlePickImage}
        />
      )}
      {isHowItWorksModalOpen && selectedPresetId !== "free-prompt" && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setIsHowItWorksModalOpen(false)}
        >
          <div
            className="w-full max-w-6xl max-h-[90vh] overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={t("video.howItWorks.eyebrow")}
          >
            <HowItWorksCard
              presetId={selectedPresetId}
              presetLabel={selectedPresetLabel}
              hasEndFrameInput={supportsEndFrame}
              onDismiss={() => setIsHowItWorksModalOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const Route = createFileRoute("/projects/$projectId/video")({
  component: VideoStudio,
});
